import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { api } from "@/api.ts";
import type { UserParameters } from "@/types";
import { emptyUserParameters } from "@/types";
import { parseAccessToken } from "@/helpers";
import { createMetaPutScheduler } from "@/utils/metaPutScheduler";
import { ApiHttpError } from "@/utils/apiHttpError";

// The initial GET /users/meta races the previous page's keepalive PUT to the
// same path on reload: the PUT bypasses the route-rate gate, so both land in the
// backend's 1-req/sec/route window and the GET gets a 429. The gate's single
// retry is not always enough, so load() retries a few more times with a delay
// long enough to clear that window. Retryable = 429/5xx/network, never abort.
export const LOAD_MAX_RETRIES = 3;
export const LOAD_RETRY_DELAY_MS = 1200;

function isRetryableLoadError(err: unknown): boolean {
  if (err instanceof ApiHttpError) {
    return err.status === 429 || err.status >= 500;
  }
  return err instanceof TypeError;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const STORAGE_KEYS = {
  layout_info: "layout_info",
  graph_settings: "graph_settings",
  folded_menu: "folded_menu",
  locale: "locale",
  is_tutorial_passed: "is_tutorial_passed",
  card_width: "card_width",
  network_map_filters: "network_map_filters",
  show_unallocated_nodes: "show_unallocated_nodes",
} as const;

function setLocal(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

export const useUserSettingsStore = defineStore("settings.user", () => {
  const params = ref<UserParameters | null>(null);
  const status = ref<"idle" | "loading" | "ready" | "error">("idle");
  const error = ref<string | null>(null);
  const _defaultLocalSnapshot = emptyUserParameters();

  const isLoading = computed(() => status.value === "loading");
  const isReady = computed(() => status.value === "ready");
  const isLoaded = computed(
    () => status.value === "ready" || status.value === "error",
  );

  function applyPartialToParams(partial: Partial<UserParameters>) {
    const base = params.value ?? emptyUserParameters();
    params.value = { ...base, ...partial };
    syncLocalFromParams();
  }

  const metaPutScheduler = createMetaPutScheduler({
    put: async (partial, opts) => {
      if (opts?.keepalive) {
        await api.putUserParameter(partial, undefined, { keepalive: true });
      } else {
        await api.putUserParameter(partial);
      }
      applyPartialToParams(partial);
    },
  });

  function syncLocalFromParams() {
    if (!params.value) return;
    setLocal(STORAGE_KEYS.graph_settings, params.value.graph_settings);
    setLocal(STORAGE_KEYS.layout_info, params.value.layout_info);
    setLocal(STORAGE_KEYS.folded_menu, params.value.folded_menu);
    setLocal(STORAGE_KEYS.locale, params.value.locale);
    setLocal(STORAGE_KEYS.is_tutorial_passed, params.value.is_tutorial_passed);
    setLocal(STORAGE_KEYS.card_width, params.value.card_width);
    setLocal(STORAGE_KEYS.network_map_filters, params.value.network_map_filters);
    setLocal(
      STORAGE_KEYS.show_unallocated_nodes,
      params.value.show_unallocated_nodes,
    );
  }

  async function load() {
    status.value = "loading";
    error.value = null;
    let attempt = 0;
    try {
      for (;;) {
        try {
          const data = await api.getUserParameters();
          params.value = data;
          syncLocalFromParams();
          status.value = "ready";
          return data;
        } catch (err) {
          if (!isRetryableLoadError(err) || attempt >= LOAD_MAX_RETRIES) {
            throw err;
          }
          attempt += 1;
          await sleep(LOAD_RETRY_DELAY_MS);
        }
      }
    } catch (err: any) {
      status.value = "error";
      error.value = String(err);
      throw err;
    }
  }

  async function setAll(next: UserParameters) {
    if (parseAccessToken() == null) {
      return params.value ?? emptyUserParameters();
    }
    await api.setUserParameters(next);
    params.value = next;
    syncLocalFromParams();
    return params.value;
  }

  async function patch(partial: Partial<UserParameters>) {
    if (parseAccessToken() == null) {
      return params.value ?? emptyUserParameters();
    }
    await api.putUserParameter(partial);
    applyPartialToParams(partial);
    return params.value;
  }

  function scheduleMetaPut(partial: Partial<UserParameters>) {
    if (parseAccessToken() == null) return;
    metaPutScheduler.enqueue(partial);
  }

  async function flushMetaPut() {
    if (parseAccessToken() == null) return;
    await metaPutScheduler.flush();
  }

  function setLocale(locale: UserParameters["locale"]) {
    scheduleMetaPut({ locale });
  }

  function setLayoutInfo(layout_info: UserParameters["layout_info"]) {
    scheduleMetaPut({ layout_info });
  }

  function setGraphSettings(graph_settings: UserParameters["graph_settings"]) {
    // A later meta sync rewrites every local key from params.
    if (params.value) {
      params.value = { ...params.value, graph_settings };
    }
    setLocal(STORAGE_KEYS.graph_settings, graph_settings);
    scheduleMetaPut({ graph_settings });
  }

  function setFoldedMenu(folded_menu: UserParameters["folded_menu"]) {
    scheduleMetaPut({ folded_menu });
  }

  function setTutorialPassed(is_tutorial_passed: boolean) {
    scheduleMetaPut({ is_tutorial_passed });
  }

  function setNetworkMapFilters(
    network_map_filters: UserParameters["network_map_filters"],
  ) {
    scheduleMetaPut({ network_map_filters });
  }

  function setShowUnallocatedNodes(show_unallocated_nodes: boolean) {
    scheduleMetaPut({ show_unallocated_nodes });
  }

  function reset() {
    metaPutScheduler.reset();
    params.value = null;
    status.value = "idle";
    error.value = null;

    // Local UI settings are user-bound; drop them to defaults on logout.
    setLocal(STORAGE_KEYS.graph_settings, _defaultLocalSnapshot.graph_settings);
    setLocal(STORAGE_KEYS.layout_info, _defaultLocalSnapshot.layout_info);
    setLocal(STORAGE_KEYS.folded_menu, _defaultLocalSnapshot.folded_menu);
    setLocal(STORAGE_KEYS.locale, _defaultLocalSnapshot.locale);
    setLocal(
      STORAGE_KEYS.is_tutorial_passed,
      _defaultLocalSnapshot.is_tutorial_passed,
    );
    setLocal(STORAGE_KEYS.card_width, _defaultLocalSnapshot.card_width);
    setLocal(
      STORAGE_KEYS.network_map_filters,
      _defaultLocalSnapshot.network_map_filters,
    );
    setLocal(
      STORAGE_KEYS.show_unallocated_nodes,
      _defaultLocalSnapshot.show_unallocated_nodes,
    );
  }

  return {
    params,
    status,
    isLoading,
    isReady,
    isLoaded,
    error,
    load,
    setAll,
    patch,
    scheduleMetaPut,
    flushMetaPut,
    setLocale,
    setLayoutInfo,
    setGraphSettings,
    setFoldedMenu,
    setTutorialPassed,
    setNetworkMapFilters,
    setShowUnallocatedNodes,
    reset,
  };
});
